<template>
  <div class="w-full">
    <div class="my-3">
      <h1 class="h1 text-grey-light-900 font-semibold text-3xl mb-2">Merchant Sign Up</h1>
      <p class="text-sm font-normal text-black mb-2">Please fill in your details below.</p>
      <p v-if="errorMessage" class="text-error-700">{{ errorMessage }}</p>
    </div>

    <form id="signUpForm" class="text-left">
      <TextInput
        id="name"
        label="Name"
        placeholder="Enter your name"
        :regex="/^[A-Za-zÀ-ÖØ-öø-ÿ]+(?:[-'][A-Za-zÀ-ÖØ-öø-ÿ]+)*$/"
        :disabled="isLoading"
        test-id="name"
        @error="
          (error: string) => {
            errorMessage = error;
          }
        "
        v-model:input="firstName"
        v-model:is-input-valid="isFirstNameValid"
      />

      <TextInput
        id="surname"
        label="Surname"
        placeholder="Enter your surname"
        :regex="/^[A-Za-zÀ-ÖØ-öø-ÿ]+(?:[-'][A-Za-zÀ-ÖØ-öø-ÿ]+)*$/"
        :disabled="isLoading"
        test-id="surname"
        @error="
          (error: string) => {
            errorMessage = error;
          }
        "
        v-model:input="surname"
        v-model:is-input-valid="isSurnameValid"
      />

      <UsernameInput
        id="email"
        v-model:username="email"
        v-model:is-username-valid="isEmailValid"
        placeholder="Enter your company email"
        label="Email"
        type="EMAIL"
        :disabled="isLoading"
        @error="
          (error: string) => {
            errorMessage = error;
          }
        "
      />

      <UsernameInput
        v-model:username="cellNumber"
        v-model:is-username-valid="isCellNumberValid"
        placeholder="Enter your mobile number"
        label="Mobile Number"
        id="mobile"
        type="MOBILE"
        test-id="mobile"
        :disabled="isLoading"
        @error="
          (error: string) => {
            errorMessage = error;
          }
        "
      />

      <TextInput
        id="companyName"
        label="Company registered name"
        placeholder="Company registered name"
        test-id="company_name"
        :disabled="isLoading"
        v-model:input="companyName"
        v-model:is-input-valid="isCompanyNameValid"
      />
      <TurnstileWidget @success="handleSuccess" />
      <LayupButton
        type="submit"
        :disabled="
          isLoading ||
          !isFirstNameValid ||
          !isEmailValid ||
          !isCompanyNameValid ||
          !isCellNumberValid ||
          !turnstileToken
        "
        data-testid="sign_up_button"
        :isLoading="isLoading"
        :label="'Sign up'"
        @click="onSubmit()"
      />

      <div class="text-center mt-3 text-sm text-black">
        <p>
          Already have an account?
          <RouterLink to="sign-in" class="btn text-brand-700 p-0 text-sm"> Sign in </RouterLink>
        </p>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import LayupButton from '@/components/common/LayupButton.vue';
import TextInput from '@/components/common/TextInput.vue';
import TurnstileWidget from '@/components/common/TurnstileWidget.vue';
import UsernameInput from '@/components/common/UsernameInput.vue';
import { BASE_URL } from '@/config';
import { NetworkError } from '@/errors';
import { getErrorMessage, signUpMerchant } from '@/services/auth';
import { useGeneralStore } from '@/stores/general';
import { useUserStore } from '@/stores/users';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  channel: string;
}>();

const userStore = useUserStore();
const generalStore = useGeneralStore();
const router = useRouter();

const firstName = ref('');
const isFirstNameValid = ref(false);
const surname = ref('');
const isSurnameValid = ref(false);
const email = ref('');
const isEmailValid = ref(false);
const cellNumber = ref('');
const isCellNumberValid = ref(false);
const companyName = ref('');
const isCompanyNameValid = ref(false);
const turnstileToken = ref('');

const errorMessage = ref('');
const isLoading = ref(false);

const user = ref({
  _id: '',
  name: '',
  address: '',
  city: '',
  country: '',
  province: '',
  postalCode: '',
  email: '',
  role: 'MERCHANT',
  cellNumber: '',
  redirectTo: `${BASE_URL}/email-verification-success`,
  merchantOnboarding: {
    name: '',
    physicalAddress: {
      country: '',
      province: '',
      streetAddress: '',
      city: '',
      postalCode: '',
    },
  } as any,
} as any);

onMounted(() => {
  // Clear the store when this page is loaded to make sure we don't have any stale data
  userStore.reset();
});

const handleSuccess = (token: string) => {
  turnstileToken.value = token;
};

const onSubmit = async () => {
  errorMessage.value = '';

  if (!isFirstNameValid.value || !isEmailValid.value || !isCompanyNameValid.value || !isCellNumberValid.value) {
    errorMessage.value = 'Please make sure all fields are filled out correctly before submitting.';
    return;
  }

  try {
    isLoading.value = true;
    user.value.name = `${firstName.value} ${surname.value}`;
    user.value.email = email.value;
    user.value.cellNumber = cellNumber.value.replace(/\s+/g, '');
    user.value.merchantOnboarding.name = companyName.value;

    const response = await signUpMerchant(user.value, turnstileToken.value);
    userStore.user = response;
    // Set this to true so that when mobile verification is complete it will redirect to create password screen
    generalStore.passwordReset = true;

    addMetric('sign-up', { status: 'success', channel: props.channel });
    router.push({ name: 'merchantOtpVerification' });
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('sign-up', { status: 'network_error', channel: props.channel });
      errorMessage.value = 'Network Error. Please try again.';
    } else {
      createException(error);
      addMetric('sign-up', { status: 'error', channel: props.channel });
      errorMessage.value = getErrorMessage(error);
    }
    isLoading.value = false;
  }
};

onUnmounted(() => {
  isLoading.value = false;
});
</script>
